import { useNavigate } from "react-router";
import { useState } from "react";

import Banner from "ds/components/Banner";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerHeader from "ds/components/Drawer/Header";
import Typography from "ds/components/Typography";
import Box from "ds/components/Box";
import Counter from "ds/components/Counter";
import { StackDependency } from "types/generated";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import DrawerForm from "ds/components/DrawerNew/Form";
import Toggle from "ds/components/Toggle";
import Card from "ds/components/Card";
import useUpdateStackDependency from "shared/Stack/useUpdateStackDependency";

import OutputReferencesDrawerForm from "./Form";
import OutputReferencesDrawerListItem from "./ListItem";
import StackDependenciesOutputReferencesContextProvider from "./Context";


type OutputReferencesDrawerProps = {
  stackDependency: StackDependency;
  stackIdSourceOfOutputs: string;
};

export const OutputReferencesDrawer = createDrawer(
  ({ stackDependency, stackIdSourceOfOutputs }: OutputReferencesDrawerProps) => {
    const navigate = useNavigate();
    const [isDirty, setIsDirty] = useState(false);
    const [updateStackDependency] = useUpdateStackDependency({
      refetchQueries: ["GetStack"],
    });

    const handleGoToOutput = (anchor: string) => {
      navigate(`/stack/${stackIdSourceOfOutputs}/outputs#${anchor}`);
    };

    const handleTriggerAlwaysChange = (triggerAlways: boolean) => {
      updateStackDependency({
        input: {
          id: stackDependency.id,
          triggerAlways,
        },
      });
    };

    return (
      <StackDependenciesOutputReferencesContextProvider
        stackIdSourceOfOutputs={stackIdSourceOfOutputs}
      >
        <DrawerForm isDirty={isDirty}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Manage output references" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            <Box direction="column" gap="large">
              <Banner variant="info">
                If you add an output reference, this stack will run only when at least one of the
                outputs added below changes.{" "}
              </Banner>

              <Card variant="secondary" contentGap="large">
                <Toggle
                  variant="switch"
                  isSelected={stackDependency.triggerAlways}
                  onChange={handleTriggerAlwaysChange}
                  aria-label="Always trigger runs on this dependency"
                >
                  <Box direction="column" gap="small">
                    <Typography tag="span" variant="p-body3">
                      Always trigger runs on this dependency
                    </Typography>
                    <Typography tag="span" variant="p-body4" color="secondary">
                      Trigger dependent runs even when outputs haven't changed
                    </Typography>
                  </Box>
                </Toggle>
              </Card>

              <OutputReferencesDrawerForm
                setIsDirty={setIsDirty}
                stackDependencyId={stackDependency.id}
                vendorTypename={stackDependency.stack.vendorConfig?.__typename}
              />

              {stackDependency.referenceCount > 0 && (
                <Box direction="column">
                  <Box gap="small">
                    <Typography tag="h5" variant="p-t5">
                      Output references
                    </Typography>

                    <Counter count={stackDependency.referenceCount} size="small" />
                  </Box>
                </Box>
              )}

              {stackDependency.references?.map((outputReference) => (
                <OutputReferencesDrawerListItem
                  setIsDirty={setIsDirty}
                  key={outputReference.id}
                  stackDependencyId={stackDependency.id}
                  outputReference={outputReference}
                  onGoToOutput={handleGoToOutput}
                />
              ))}
            </Box>
          </DrawerBody>
        </DrawerForm>
      </StackDependenciesOutputReferencesContextProvider>
    );
  }
);

export const showOutputReferencesDrawer = createDrawerTrigger(OutputReferencesDrawer);
